import { useState, useEffect, useRef } from "react";
import {
  Header,
  ResearchStatus,
  LegalQueryForm,
  LegalReport,
} from "./components";
import type {
  ResearchStatusType,
  LegalQueryReport,
  SSEEvent,
  ResearchProgress,
} from "./types";
import { glassStyle, fadeInAnimation } from "./styles";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

type PhaseType =
  | "intent"
  | "research"
  | "extraction"
  | "analysis"
  | "reasoning"
  | "checklist"
  | "complete"
  | null;

function App() {
  const [isResearching, setIsResearching] = useState(false);
  const [status, setStatus] = useState<ResearchStatusType | null>(null);
  const [report, setReport] = useState<LegalQueryReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const [currentPhase, setCurrentPhase] = useState<PhaseType>(null);
  const [isResetting, setIsResetting] = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToStatus, setHasScrolledToStatus] = useState(false);

  // Add new state for color cycling
  const [loaderColor, setLoaderColor] = useState("#7C3AED");

  // Add new state for SSE events and progress tracking
  const [events, setEvents] = useState<SSEEvent[]>([]);
  const [progress, setProgress] = useState<ResearchProgress>({
    officialSourcesCount: 0,
    newsSourcesCount: 0,
    totalSourcesCount: 0,
    claimsCount: 0,
    contradictionsCount: 0,
    checklistTotal: 0,
    checklistRequired: 0,
    checklistRecommended: 0,
  });

  // Scroll helper function
  const scrollToStatus = () => {
    if (!hasScrolledToStatus && statusRef.current) {
      const yOffset = -20;
      const y =
        statusRef.current.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setHasScrolledToStatus(true);
    }
  };

  // Color cycling effect
  useEffect(() => {
    if (!isResearching) return;

    const colors = [
      "#7C3AED", // Violet
      "#A78BFA", // Light Violet
      "#06B6D4", // Cyan
      "#22D3EE", // Light Cyan
      "#10B981", // Emerald
      "#34D399", // Light Emerald
    ];

    let currentIndex = 0;

    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % colors.length;
      setLoaderColor(colors[currentIndex]);
    }, 1000);

    return () => clearInterval(interval);
  }, [isResearching]);

  const resetState = () => {
    setIsResetting(true);

    setTimeout(() => {
      setStatus(null);
      setReport(null);
      setError(null);
      setIsComplete(false);
      setCurrentPhase(null);
      setHasScrolledToStatus(false);
      setIsResetting(false);
      setEvents([]);
      setProgress({
        officialSourcesCount: 0,
        newsSourcesCount: 0,
        totalSourcesCount: 0,
        claimsCount: 0,
        contradictionsCount: 0,
        checklistTotal: 0,
        checklistRequired: 0,
        checklistRecommended: 0,
      });
    }, 300);
  };

  // Map node names to user-friendly step names
  const getStepName = (
    nodeName: string,
  ): { step: string; phase: PhaseType } => {
    const stepMap: Record<string, { step: string; phase: PhaseType }> = {
      intent_analysis: { step: "Intent Analysis", phase: "intent" },
      legal_research: { step: "Legal Research", phase: "research" },
      claim_extraction: { step: "Extracting Claims", phase: "extraction" },
      contradiction_analysis: {
        step: "Analyzing Contradictions",
        phase: "analysis",
      },
      legal_reasoning: { step: "Legal Reasoning", phase: "reasoning" },
      compliance_checklist: { step: "Building Checklist", phase: "checklist" },
    };
    return stepMap[nodeName] || { step: nodeName, phase: null };
  };

  // Stream research results via SSE
  const streamResults = (jobId: string) => {
    const eventSource = new EventSource(`${API_URL}/query/${jobId}/stream`);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as SSEEvent;

        // Store all events
        setEvents((prev) => [...prev, data]);

        // Handle progress events from backend (node transitions)
        if (data.type === "progress" && data.step) {
          const { step, phase } = getStepName(data.step);
          setStatus({
            step: step,
            message: `Processing ${data.step.replace(/_/g, " ")}...`,
          });
          setCurrentPhase(phase);
          scrollToStatus();
        }

        // Handle custom events pushed by nodes
        if (data.type && data.message) {
          setStatus({
            step: data.step || "Processing",
            message: data.message,
          });
          scrollToStatus();
        }

        // Process event-specific data for progress tracking
        processEventData(data);

        // Handle completion event
        if (data.type === "complete" && data.report) {
          setReport(data.report);
          setStatus({
            step: "Complete",
            message: "Analysis completed successfully",
          });
          setCurrentPhase("complete");
          setIsComplete(true);
          setIsResearching(false);
          eventSource.close();
        }

        // Handle error event
        if (data.type === "error") {
          setError(data.error || "Unknown error");
          setIsResearching(false);
          eventSource.close();
        }
      } catch (err) {
        console.error("Error parsing SSE data:", err);
      }
    };

    eventSource.onerror = () => {
      setError("Connection lost or server error");
      setIsResearching(false);
      eventSource.close();
    };
  };

  // Process event data to update progress state
  const processEventData = (data: SSEEvent) => {
    switch (data.type) {
      // Intent events
      case "intent_extracted":
        setProgress((prev) => ({
          ...prev,
          intent: data.intent,
          topics: data.topics,
          isAnswerable: data.is_answerable,
        }));
        break;

      // Research events
      case "queries_generated":
        setProgress((prev) => ({
          ...prev,
          queries: data.queries,
        }));
        break;

      case "research_official":
        setProgress((prev) => ({
          ...prev,
          officialSourcesCount: prev.officialSourcesCount + (data.count || 0),
        }));
        break;

      case "research_news":
        setProgress((prev) => ({
          ...prev,
          newsSourcesCount: prev.newsSourcesCount + (data.count || 0),
        }));
        break;

      case "research_complete":
        setProgress((prev) => ({
          ...prev,
          totalSourcesCount: data.count || 0,
        }));
        break;

      // Extraction events
      case "extraction_complete":
        setProgress((prev) => ({
          ...prev,
          claimsCount: data.total_claims || 0,
          claimsByCategory: data.by_category,
        }));
        break;

      // Contradiction events
      case "contradiction_complete":
        setProgress((prev) => ({
          ...prev,
          contradictionsCount: data.contradictions || 0,
        }));
        break;

      // Reasoning events
      case "reasoning_complete":
        setProgress((prev) => ({
          ...prev,
          verdict: data.verdict,
          confidence: data.confidence,
          keyFactors: data.key_factors,
          primarySources: data.primary_sources,
        }));
        break;

      // Checklist events
      case "checklist_complete":
        setProgress((prev) => ({
          ...prev,
          checklistTotal: data.total || 0,
          checklistRequired: data.required || 0,
          checklistRecommended: data.recommended || 0,
        }));
        break;
    }
  };

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Handle form submission
  const handleFormSubmit = async (formData: {
    query: string;
    country: string;
    subdivision: string;
  }) => {
    // Clear any existing errors first
    setError(null);

    // If research is complete, reset the UI first
    if (isComplete) {
      resetState();
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    // Clear any existing SSE connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setIsResearching(true);
    setStatus({
      step: "Starting",
      message: "Initiating legal analysis...",
    });

    try {
      const url = `${API_URL}/query/async`;

      const requestData = {
        query: formData.query,
        country: formData.country,
        subdivision: formData.subdivision || null,
      };

      const response = await fetch(url, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.job_id) {
        streamResults(data.job_id);
      } else {
        throw new Error("No job ID received");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start analysis");
      setIsResearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A12] p-6 md:p-10 relative overflow-hidden">
      {/* Gradient orbs - repositioned */}
      <div className="absolute top-20 -left-40 w-[500px] h-[500px] bg-[#7C3AED]/8 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-[#06B6D4]/8 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/3 w-[300px] h-[300px] bg-[#10B981]/5 rounded-full blur-[100px] pointer-events-none"></div>

      {/* Subtle noise texture */}
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
        }}
      ></div>

      {/* Diagonal lines accent */}
      <div className="absolute top-0 right-0 w-96 h-96 overflow-hidden pointer-events-none opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #7C3AED 0, #7C3AED 1px, transparent 0, transparent 50%)",
            backgroundSize: "10px 10px",
          }}
        ></div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header Component */}
        <Header />

        {/* Form Section */}
        <LegalQueryForm
          onSubmit={handleFormSubmit}
          isResearching={isResearching}
          glassStyle={glassStyle}
          loaderColor={loaderColor}
        />

        {/* Error Message */}
        {error && (
          <div
            className={`mt-6 p-4 rounded-xl border-l-4 border-[#EF4444] bg-[#EF4444]/5 ${fadeInAnimation.fadeIn} ${isResetting ? "opacity-0 transform -translate-y-4" : "opacity-100 transform translate-y-0"}`}
          >
            <p className="text-[#EF4444] text-sm">{error}</p>
          </div>
        )}

        {/* Status Box */}
        <div className="mt-8">
          <ResearchStatus
            status={status}
            error={error}
            isComplete={isComplete}
            currentPhase={currentPhase}
            isResetting={isResetting}
            glassStyle={glassStyle}
            loaderColor={loaderColor}
            statusRef={statusRef}
            events={events}
            progress={progress}
          />
        </div>

        {/* Legal Report */}
        {report && (
          <div className="mt-8">
            <LegalReport
              report={report}
              isResetting={isResetting}
              glassStyle={glassStyle}
              fadeInAnimation={fadeInAnimation}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-[10px] text-[#3D3D5C] tracking-wider">
          POWERED BY AI · NOT LEGAL ADVICE
        </p>
      </div>
    </div>
  );
}

export default App;
